import type { BillingCycle, PlanCode } from './prototypeTypes';
import { normalizePurchasableBillingCycle } from '../../domain';
import { planStore } from './planStore';
import type { ChooserSource } from './subscriptionChooserState';

const STORAGE_KEY = 'deepmox.pending-checkout';
const MAX_AGE_MS = 30 * 60 * 1000;
const CHECKOUT_PLAN_ALLOWLIST = new Set<PlanCode>(['lite', 'pro', 'max']);
const BILLING_CYCLES = new Set<BillingCycle>(['monthly', 'quarterly', 'annual']);

interface PendingCheckoutIntent {
	plan: 'lite' | 'pro' | 'max';
	billingCycle: BillingCycle;
	source: ChooserSource;
	recordedAt: number;
	boundUserId: string | null;
}

/** Persists only allowlisted checkout selection through cross-tab email verification. */
export function recordPendingCheckoutIntent(plan: 'lite' | 'pro' | 'max', billingCycle: BillingCycle, source: ChooserSource = 'pricing'): void {
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ plan, billingCycle, source, recordedAt: Date.now(), boundUserId: null }));
	} catch {
		// Storage can be unavailable in private mode; signup itself still works.
	}
}

export function readPendingCheckoutIntent(): PendingCheckoutIntent | null {
	if (typeof localStorage === 'undefined') return null;
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Partial<PendingCheckoutIntent>;
		if (!CHECKOUT_PLAN_ALLOWLIST.has(value.plan as PlanCode)
			|| !BILLING_CYCLES.has(value.billingCycle as BillingCycle)
			|| !isChooserSource(value.source)
			|| typeof value.recordedAt !== 'number'
			|| !(value.boundUserId === null || typeof value.boundUserId === 'string')
			|| Date.now() - value.recordedAt > MAX_AGE_MS
			|| value.recordedAt > Date.now() + 60_000) {
			clearPendingCheckoutIntent();
			return null;
		}
		return value as PendingCheckoutIntent;
	} catch {
		clearPendingCheckoutIntent();
		return null;
	}
}

/** Binds a newly registered checkout intent to the server-issued user id. */
export function bindPendingCheckoutIntentToUser(userId: string): void {
	const intent = readPendingCheckoutIntent();
	if (!intent || !userId || typeof localStorage === 'undefined') return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...intent, boundUserId: userId }));
	} catch {
		// The unbound TTL still prevents durable stale state if storage becomes unavailable.
	}
}

export function clearPendingCheckoutIntent(): void {
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing else can safely be done when storage is unavailable.
	}
}

export async function replayPendingCheckoutIntent(
	userId: string,
	execute: (plan: 'lite' | 'pro' | 'max', billingCycle: BillingCycle, source: ChooserSource) => Promise<void>,
): Promise<'none' | 'user_mismatch' | 'deferred' | 'replayed'> {
	const intent = readPendingCheckoutIntent();
	if (!intent) return 'none';
	if (intent.boundUserId && intent.boundUserId !== userId) return 'user_mismatch';
	const hydratedPlans = planStore.getAll();
	if (!hydratedPlans) return 'deferred';
	const billingCycle = normalizePurchasableBillingCycle(intent.billingCycle, Object.values(hydratedPlans));
	clearPendingCheckoutIntent();
	if (!billingCycle) return 'none';
	await execute(intent.plan, billingCycle, intent.source);
	return 'replayed';
}

function isChooserSource(value: unknown): value is ChooserSource {
	return value === 'paywall' || value === 'avatar' || value === 'profile' || value === 'pricing' || value === 'ai-gate' || value === 'github-gate';
}
